import type { Breadcrumb, ErrorBoundary as ErrorBoundaryType, User } from '@sentry/react';

import { groupedCaptureArgs } from './captureGrouping';

export { browserTracingIntegration } from '@sentry/browser';

export { groupedCaptureArgs, messageFingerprint } from './captureGrouping';
export { isIgnoredError, isIgnoredEvent } from './utils';

export interface SentryMonitorSdk {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureException: (exception: unknown, ...args: any[]) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureEvent: (...args: any[]) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureMessage: (message: string, ...args: any[]) => string;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
  flush: (timeout?: number) => Promise<boolean>;
  setUser: (user: User | null) => void;
  setTag: (key: string, value: string) => void;
  ErrorBoundary: typeof ErrorBoundaryType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  withSentryConfig?: (...args: any[]) => any;
}

export function createMonitor(sdk: SentryMonitorSdk) {
  const captureException = (exception: unknown, context?: unknown): string => {
    process.env.NODE_ENV !== 'production' && console.error(exception, context);
    return sdk.captureException(...groupedCaptureArgs(exception, context, captureException));
  };

  return {
    captureException,
    captureEvent: (...args: Parameters<typeof sdk.captureEvent>) => {
      process.env.NODE_ENV !== 'production' && console.log(...args);
      return sdk.captureEvent(...args);
    },
    captureMessage: (...args: Parameters<typeof sdk.captureMessage>) => {
      process.env.NODE_ENV !== 'production' && console.info(...args);
      return sdk.captureMessage(...args);
    },
    ErrorBoundary: sdk.ErrorBoundary,
    addBreadcrumb: sdk.addBreadcrumb,
    flush: sdk.flush,
    withSentryConfig: sdk.withSentryConfig,
    setUser: sdk.setUser,
    setTag: sdk.setTag,
  };
}

export { createMonitoringProvider } from './MonitoringProvider';
